"use client";

import type { FirmType, UserType } from "@/shared/graphql/generated/graphql";

interface PendoConfig {
  visitor: { id?: string; [key: string]: any };
  account?: { id?: string; [key: string]: any };
}

declare global {
  interface Window {
    pendo?: {
      initialize: (config: PendoConfig) => void;
      identify: (config: PendoConfig) => void;
      updateOptions: (options: any) => void;
      pageLoad: (props?: Record<string, any>) => void;
      track: (event: string, metadata?: Record<string, any>) => void;
      _q?: any[];
    };
  }
}

export const ANONYMOUS_ID = "VISITOR-UNIQUE-ID";

const isBrowser = (): boolean => typeof window !== "undefined";

const getPendo = () => (isBrowser() ? window.pendo : undefined);

let agentInjected = false;
const ensureAgent = (): void => {
  const apiKey = process.env.NEXT_PUBLIC_PENDO_API_KEY;

  if (!isBrowser() || !apiKey) return;
  if (agentInjected || getPendo()) return;

  /* eslint-disable no-var, prefer-rest-params */
  (function (key: string) {
    (function (p: any, e: Document, n: string, d: string) {
      var v: string[],
        w: number,
        x: number,
        y: HTMLScriptElement,
        z: Element | null;
      var o = (p[d] = p[d] || {});
      o._q = o._q || [];
      v = ["initialize", "identify", "updateOptions", "pageLoad", "track"];
      for (w = 0, x = v.length; w < x; ++w) {
        (function (m: string) {
          o[m] =
            o[m] ||
            function () {
              o._q[m === v[0] ? "unshift" : "push"](
                [m].concat([].slice.call(arguments, 0)),
              );
            };
        })(v[w]);
      }
      y = e.createElement(n) as HTMLScriptElement;
      y.async = true;
      y.src = "https://cdn.pendo.io/agent/static/" + key + "/pendo.js";
      z = e.getElementsByTagName(n)[0];
      z?.parentNode?.insertBefore(y, z);
    })(window, document, "script", "pendo");
  })(apiKey!);
  /* eslint-enable no-var, prefer-rest-params */

  agentInjected = true;
};

const prefixForEnv = (id: number | string): string => {
  if (id === ANONYMOUS_ID) return `${id}`;
  const env = process.env.NODE_ENV;
  const isProd = env === "production";
  return isProd ? `${id}` : `${env}_${id}`;
};

export const initPendo = (): void => {
  ensureAgent();
  const pendo = getPendo();
  if (!pendo) return;

  try {
    const cfg: PendoConfig = {
      visitor: { id: prefixForEnv(ANONYMOUS_ID) },
    };
    pendo.initialize(cfg);
  } catch (err) {
    console.log("Error initializing Pendo", err);
  }
};

export const identifyUser = (
  user: UserType,
  firm?: FirmType,
  role?: string,
  familyId?: string,
): void => {
  ensureAgent();
  const pendo = getPendo();
  if (!pendo || !user?.id) return;

  const cfg: PendoConfig = {
    visitor: {
      id: prefixForEnv(user.id),
      email: user.email ?? undefined,
      full_name:
        `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || undefined,
      ...(familyId && { familyId: familyId ?? undefined }),
      role: role ?? undefined,
      program: process.env.NEXT_PROGRAM_NAME ?? "TG",
      firmId: firm?.id ?? undefined,
      firmName: firm?.firmName ?? undefined,
    },
  };

  if (firm?.id) {
    cfg.account = {
      id: prefixForEnv(firm.id),
      name: firm?.firmName ?? undefined,
    };
  }
  pendo.identify(cfg);
};

export const track = (event: string, metadata?: Record<string, any>): void => {
  getPendo()?.track?.(event, metadata);
};

export const pageLoad = (props?: Record<string, any>): void => {
  getPendo()?.pageLoad?.(props);
};
